import React, {useCallback, useEffect, useMemo, useState} from 'react';
import { Table, TableBody, TableContainer } from '@mui/material';

import { complainsTableHead } from '../app/config';
import {
    ComplainsTableRow,
    TableHeader,
    TablePagination,
} from '../components/table';

import useComplaints from '../hooks/useComplaints';

import './screens.styles.scss';
import '../components/table/table.styles.scss';
import {useLocation, useSearchParams} from "react-router-dom";
import ComplainsTableFilterNew from "../components/table/filters/complainsTableFilterNew";

const toUrlObj = (f) => {
    const obj = {};
    if (f.pageSize)         obj.pageSize = String(f.pageSize);
    if (f.pageNumber)       obj.pageNumber = String(f.pageNumber);
    if (f.orderBy)          obj.orderBy = f.orderBy;
    if (f.orderDirection)   obj.orderDirection = f.orderDirection;
    if (f.search)           obj.search = f.search;
    return obj;
};


const fromUrl = (sp) => {
    const get = (k) => sp.get(k);
    const num = (v, d) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : d;
    };
    const dir = (v) => (v === 'asc' || v === 'desc' ? v : null);

    return {
        pageSize:       num(get('pageSize'), 6),
        pageNumber:     num(get('pageNumber'), 1),
        orderBy:        get('orderBy') || null,
        orderDirection: dir(get('orderDirection')),
        search:         get('search') || '',
    };
};

const sameParams = (a, b) => {
    const A = Object.entries(a).filter(([,v]) => v !== null && v !== undefined && v !== '');
    const B = Object.entries(b).filter(([,v]) => v !== null && v !== undefined && v !== '');
    if (A.length !== B.length) return false;
    return A.every(([k, v]) => String(v) === String(b[k]));
};


const ComplainsScreen = () => {
    const [open, setOpen] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();


    const initial = useMemo(() => fromUrl(searchParams), []);

    const {
        data: complains,
        error,
        filter,
        setFilter,
        nextPage,
        previousPage,
        refresh,
        pagination,
    } = useComplaints(initial);

    useEffect(() => {
        const incoming = fromUrl(searchParams);
        const current  = {
            pageSize: filter.pageSize,
            pageNumber: filter.pageNumber,
            orderBy: filter.orderBy,
            orderDirection: filter.orderDirection,
            search: filter.search,
        };

        if (!location.search) {
            setSelectedItems([]);
            setOpen(null);
            return;
        }
        
        if (!sameParams(incoming, current)) {
            Object.entries(incoming).forEach(([k, v]) => setFilter(k, v));
        }
    }, [searchParams]);

    useEffect(() => {
        const desired = toUrlObj(filter);
        const current = Object.fromEntries(searchParams.entries());
        if (!sameParams(desired, current)) {
            setSearchParams(desired, { replace: true });
        }
    }, [
        filter.pageSize,
        filter.pageNumber,
        filter.orderBy,
        filter.orderDirection,
        filter.search,
        searchParams,
        setSearchParams
    ]);

    const isChecked = useCallback(
        (id) => selectedItems.some((i) => i.id === id),
        [selectedItems]
    );

    const handleRowCheck = useCallback((item, checkedNow) => {
        setSelectedItems((prev) => {
            if (!checkedNow) {
                if (prev.some((p) => p.id === item.id)) return prev;
                return [...prev, item];
            }
            return prev.filter((p) => p.id !== item.id);
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedItems((prev) => (prev.length === complains.length ? [] : complains));
    }, [complains]);

    const handleRowClick = useCallback((id) => {
        setOpen((curr) => (curr === id ? null : id));
    }, []);

    const handleRequestSort = useCallback(
        (event, property) => {
            const direction =
                filter.orderBy === property && filter.orderDirection === 'asc' ? 'desc' : 'asc';
            setFilter('orderBy', property);
            setFilter('orderDirection', direction);
        },
        [filter.orderBy, filter.orderDirection, setFilter]
    );

    const showOnPage = useCallback(
        (value) => {
            setFilter('pageSize', value);
            setFilter('pageNumber', 1);
        },
        [setFilter]
    );

    const handlePreviousPage = useCallback(() => {
        previousPage();
    }, [previousPage]);

    const handleNextPage = useCallback(() => {
        nextPage();
    }, [nextPage]);

    const rowCount = useMemo(() => complains.length, [complains]);
    const numSelected = useMemo(() => selectedItems.length, [selectedItems]);


    const onMergeFinished = () => {
        refresh();
    }

    const beforeUrl = `${location.pathname}${location.search}`;

    return (
        <div className="fk-screen">
            {error && <div style={{ padding: 8, color: 'crimson' }}>Error: {error}</div>}

            <ComplainsTableFilterNew
                filter={filter}
                setFilter={setFilter}
                onSubmit={refresh}
                selectedItems={selectedItems}
                onMergeFinished={onMergeFinished}
            />

            <TableContainer>
                <Table size="medium" className="fk-table">
                    <TableHeader
                        crud
                        withCheckbox
                        items={complainsTableHead}
                        orderBy={filter.orderBy}
                        order={filter.orderDirection}
                        numSelected={numSelected}
                        rowCount={rowCount}
                        onRequestSort={handleRequestSort}
                        onSelectAllClick={handleSelectAll}
                        screen="ComplainsScreen"
                    />
                    <TableBody>
                        {complains.map((complain) => {
                            const checked = isChecked(complain.id);
                            return (
                                <ComplainsTableRow
                                    key={complain.id}
                                    checked={checked}
                                    open={open === complain.id}
                                    complain={complain}
                                    columns={complainsTableHead}
                                    onCheck={() => handleRowCheck(complain, checked)}
                                    onRowClick={handleRowClick}
                                    before={beforeUrl}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                filter={pagination}
                handlePreviousPage={handlePreviousPage}
                handleNextPage={handleNextPage}
                showOnPage={showOnPage}
            />
        </div>
    );
};

export default ComplainsScreen;
